import type {
  AttachPdfResponse,
  Batch,
  BatchImportResponse,
  Code,
  LabelPdfFile,
  LoginResponse,
  Marketplace,
  OzonCabinet,
  OzonFreeStockRow,
  OzonImportResponse,
  OzonMatchResponse,
  OzonProduct,
  OzonRefreshResponse,
  Shipment,
  ShipmentBox,
  ShipmentListItem,
  User,
} from "./types";

const TOKEN_KEY = "teksher_token";

export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(t: string | null): void {
  if (t) localStorage.setItem(TOKEN_KEY, t);
  else localStorage.removeItem(TOKEN_KEY);
}

export class HttpError extends Error {
  status: number;
  body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  expect: "json" | "blob" | "raw" = "json",
): Promise<T> {
  const headers = new Headers(init.headers);
  const token = getToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(path, { ...init, headers });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      /* ignore */
    }
    const msg =
      (body as { detail?: string; error?: string })?.detail ||
      (body as { detail?: string; error?: string })?.error ||
      res.statusText;
    throw new HttpError(res.status, msg, body);
  }
  if (expect === "blob") return (await res.blob()) as unknown as T;
  if (expect === "raw") return res as unknown as T;
  return (await res.json()) as T;
}

export const api = {
  // auth
  login: (login: string, password: string) =>
    request<LoginResponse>("/api/auth/login", {
      method: "POST",
      body: JSON.stringify({ login, password }),
    }),
  me: () => request<User>("/api/auth/me"),

  // batches
  listBatches: () => request<Batch[]>("/api/batches"),
  getBatch: (id: number) => request<Batch>(`/api/batches/${id}`),
  uploadBatch: (
    file: File,
    comment?: string,
    labelPdfs?: File[] | null,
    ozonClientId?: number | null,
  ) => {
    const fd = new FormData();
    fd.append("file", file);
    if (comment) fd.append("comment", comment);
    if (ozonClientId != null) fd.append("ozon_client_id", String(ozonClientId));
    for (const pdf of labelPdfs ?? []) fd.append("label_pdf", pdf);
    return request<BatchImportResponse>("/api/batches/upload", {
      method: "POST",
      body: fd,
    });
  },
  attachBatchPdf: (batchId: number, files: File[]) => {
    const fd = new FormData();
    for (const pdf of files) fd.append("file", pdf);
    return request<AttachPdfResponse>(`/api/batches/${batchId}/pdf`, {
      method: "POST",
      body: fd,
    });
  },
  listBatchLabelPdfs: (batchId: number) =>
    request<LabelPdfFile[]>(`/api/batches/${batchId}/label-pdfs`),
  /** Скачать исходные PDF этикеток партии (1 файл → PDF, несколько → ZIP). */
  downloadBatchLabelPdfs: async (
    batchId: number,
  ): Promise<{ blob: Blob; filename: string }> => {
    const token = getToken();
    const res = await fetch(`/api/batches/${batchId}/label-pdfs/download`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const msg =
        (body as { detail?: string })?.detail || res.statusText || "сбой скачивания PDF";
      throw new HttpError(res.status, msg, body);
    }
    const disp = res.headers.get("content-disposition") || "";
    const m = disp.match(/filename\*=UTF-8''([^;]+)/i);
    const fallback = res.headers.get("content-type")?.includes("zip")
      ? `batch-${batchId}-labels.zip`
      : `batch-${batchId}-labels.pdf`;
    const filename = m ? decodeURIComponent(m[1]) : fallback;
    return { blob: await res.blob(), filename };
  },
  printLabels: async (gtin: string, qty: number): Promise<{ blob: Blob; filename: string }> => {
    const token = getToken();
    const res = await fetch("/api/codes/print", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ gtin, qty }),
    });
    if (!res.ok) {
      let body: unknown = null;
      try {
        body = await res.json();
      } catch {
        /* ignore */
      }
      const msg =
        (body as { detail?: string })?.detail || res.statusText || "сбой печати";
      throw new HttpError(res.status, msg, body);
    }
    const disp = res.headers.get("content-disposition") || "";
    const m = disp.match(/filename\*=UTF-8''([^;]+)/i);
    const filename = m ? decodeURIComponent(m[1]) : `codes-${gtin}-${qty}.pdf`;
    return { blob: await res.blob(), filename };
  },

  // shipments
  listShipments: () => request<ShipmentListItem[]>("/api/shipments"),
  getShipment: (id: number) => request<Shipment>(`/api/shipments/${id}`),
  createShipment: (p: {
    number: string;
    name?: string;
    ozon_client_id?: number | null;
    warehouse_id?: string;
    comment?: string;
    cluster?: number;
  }) =>
    request<Shipment>("/api/shipments", {
      method: "POST",
      body: JSON.stringify(p),
    }),
  addCodeToShipment: (shipmentId: number, p: { code_id?: number; composite_key?: string }) =>
    request<{ shipment_item_id: number; code_id: number }>(
      `/api/shipments/${shipmentId}/codes`,
      { method: "POST", body: JSON.stringify(p) },
    ),
  removeCodeFromShipment: (shipmentId: number, codeId: number) =>
    request<Response>(
      `/api/shipments/${shipmentId}/codes/${codeId}`,
      { method: "DELETE" },
      "raw",
    ),
  listShipmentCodes: (id: number) => request<Code[]>(`/api/shipments/${id}/codes/full`),
  pickShipment: (id: number, opts: { forcePartialKit?: boolean } = {}) => {
    const qs = opts.forcePartialKit ? "?force_partial_kit=true" : "";
    return request<Shipment>(`/api/shipments/${id}/pick${qs}`, { method: "POST" });
  },
  setKitMode: (id: number, isKit: boolean) =>
    request<Shipment>(`/api/shipments/${id}/kit-mode`, {
      method: "POST",
      body: JSON.stringify({ is_kit_order: isKit }),
    }),
  lockKitSize: (id: number, kitSize: number) =>
    request<Shipment>(`/api/shipments/${id}/kit-size`, {
      method: "POST",
      body: JSON.stringify({ kit_size: kitSize }),
    }),
  resetKitTraining: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/kit-reset`, { method: "POST" }),
  startNewKitBatch: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/kit-batch/start`, { method: "POST" }),
  markInTurnover: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/in-turnover`, { method: "POST" }),
  closeShipment: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/close`, { method: "POST" }),
  deleteShipment: (id: number) =>
    request<Response>(
      `/api/shipments/${id}`,
      { method: "DELETE" },
      "raw",
    ),
  rollbackShipment: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/rollback`, { method: "POST" }),
  // Стикеры коробов 58×40 мм для оклейки физических коробов отгрузки.
  listShipmentBoxes: (id: number) =>
    request<ShipmentBox[]>(`/api/shipments/${id}/boxes`),
  printBoxLabelUrl: (shipmentId: number, boxNumber: number) =>
    `/api/shipments/${shipmentId}/boxes/${boxNumber}/print`,
  activateBox: (id: number, boxNumber: number) =>
    request<Shipment>(`/api/shipments/${id}/boxes/${boxNumber}/activate`, { method: "POST" }),
  addBoxSlot: (id: number) =>
    request<Shipment>(`/api/shipments/${id}/boxes/add`, { method: "POST" }),
  finalizeBoxes: (id: number, boxesActual: number) =>
    request<Shipment>(`/api/shipments/${id}/finalize-boxes`, {
      method: "POST",
      body: JSON.stringify({ boxes_actual: boxesActual }),
    }),
  submitDelivery: (
    id: number,
    p: { marketplace: Marketplace; delivery_number: string; slot_at: string },
    markingZip: File,
  ) => {
    const fd = new FormData();
    fd.append("marketplace", p.marketplace);
    fd.append("delivery_number", p.delivery_number);
    fd.append("slot_at", p.slot_at);
    fd.append("file", markingZip);
    return request<Shipment>(`/api/shipments/${id}/delivery`, {
      method: "POST",
      body: fd,
    });
  },
  markingZipUrl: (shipmentId: number) => `/api/shipments/${shipmentId}/marking.zip`,
  exportMeta: (id: number) =>
    request<{ rows_count: number; sha256: string; filename: string }>(
      `/api/shipments/${id}/export-meta`,
    ),
  exportUrl: (id: number) => `/api/shipments/${id}/export.csv`,
  packingListUrl: (id: number) => `/api/shipments/${id}/packing-list.xlsx`,

  // ozon
  listCabinets: (includeInactive = false) =>
    request<OzonCabinet[]>(
      `/api/ozon/cabinets${includeInactive ? "?include_inactive=true" : ""}`,
    ),
  uploadOzonProducts: (file: File, ozonClientId: number) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("ozon_client_id", String(ozonClientId));
    return request<OzonImportResponse>("/api/ozon/products/upload", {
      method: "POST",
      body: fd,
    });
  },
  refreshOzonProducts: () =>
    request<OzonRefreshResponse>("/api/ozon/products/refresh", { method: "POST" }),
  listOzonProducts: (limit = 500, ozonClientId?: number | null) => {
    const qs = new URLSearchParams({ limit: String(limit) });
    if (ozonClientId != null) qs.set("ozon_client_id", String(ozonClientId));
    return request<OzonProduct[]>(`/api/ozon/products?${qs.toString()}`);
  },
  listFreeStock: () => request<OzonFreeStockRow[]>(`/api/ozon/free-stock`),
  ozonMatch: (shipmentId: number) =>
    request<OzonMatchResponse>(`/api/ozon/shipments/${shipmentId}/match`),
  ozonSupplyXlsxUrl: (shipmentId: number) =>
    `/api/ozon/shipments/${shipmentId}/supply.xlsx`,

  // codes
  getCode: (id: number) => request<Code>(`/api/codes/${id}`),
  searchCodes: (params: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "") qs.set(k, String(v));
    });
    return request<Code[]>(`/api/codes?${qs.toString()}`);
  },
};
